/**
 * Pure mapper: a discovered Azure resource (`AzureDiscoveredResource`, already
 * mapped to a connectable draft by the discovery flow) → an `ImportCandidate`
 * for the multi-source "Import connections…" review dialog.
 *
 * Kept dependency-free so it can be exercised by BOTH the renderer import flow
 * (which turns the Azure picker's picks into candidates) and the main-side
 * importer's unit test (`electron/connection-import/sources/azure.test.ts`
 * re-exports it via `azure.ts`). Candidates are keyed by the ARM resource id and
 * carry every field from `connectionDraft`; a password is NEVER read (Azure auth
 * is token/CLI-based, and a PostgreSQL password is typed during review instead).
 */
import type {
  AzureConnectionDraft,
  AzureDiscoveredResource,
} from '../../../types/azureDiscovery';
import type {
  ImportAuthLabel,
  ImportCandidate,
  ImportScanResult,
} from '../../../types/connectionImport';

/** Map a draft auth to Jam's import auth label + a short badge. */
function mapAuth(auth: AzureConnectionDraft['authentication']): {
  authentication: ImportAuthLabel;
  authLabel: string;
} {
  switch (auth) {
    case 'Azure CLI':
      return { authentication: 'Azure CLI', authLabel: 'Azure CLI' };
    case 'Password':
      return { authentication: 'Password', authLabel: 'Password' };
    case 'Microsoft Entra ID (Interactive Browser)':
    default:
      return {
        authentication: 'Microsoft Entra ID (Interactive Browser)',
        authLabel: 'Entra MFA',
      };
  }
}

/** One discovered Azure resource → one import candidate (supported, no password). */
export function mapAzureResourceToCandidate(
  resource: AzureDiscoveredResource
): ImportCandidate {
  const draft = resource.connectionDraft;
  const auth = mapAuth(draft.authentication);
  const candidate: ImportCandidate = {
    key: resource.id,
    providerName: resource.kind,
    engine: resource.engine,
    supported: true,
    alias: resource.name,
    server: draft.server,
    port: draft.port,
    database: draft.database,
    authentication: auth.authentication,
    authLabel: auth.authLabel,
    // Group rows under their subscription in the review dialog.
    groupPath: resource.subscriptionName,
  };
  if (draft.ssl) candidate.ssl = true;
  // Azure SQL / Managed Instance: the same Encrypt / Trust settings the form path applies (C4a mapper correctness).
  if (draft.encrypt !== undefined) candidate.encrypt = draft.encrypt;
  if (draft.trustServerCertificate !== undefined) candidate.trustServerCertificate = draft.trustServerCertificate;
  // Azure Storage: the endpoints, the published services and the ARM id come
  // from the Resource Graph row, so an imported account is connectable without
  // a second discovery call. A pick is always account-scope (spec §4.1).
  if (draft.azureStorageEndpoints) candidate.azureStorageEndpoints = draft.azureStorageEndpoints;
  if (draft.azureStorageServices) candidate.azureStorageServices = draft.azureStorageServices;
  if (resource.engine === 'azurestorage') candidate.azureStorageScope = { kind: 'account' };
  if (draft.armResourceId) {
    candidate.azureResourceId = draft.armResourceId;
    candidate.azureResourceIdOrigin = 'picker';
  }
  return candidate;
}

/** Wrap a set of discovered resources into a ready-to-review scan result. */
export function azureResourcesToScanResult(
  resources: AzureDiscoveredResource[]
): ImportScanResult {
  return {
    sourceId: 'azure',
    found: true,
    candidates: resources.map(mapAzureResourceToCandidate),
  };
}
